import { useSelector } from 'react-redux';
import type { RootState } from '../store';

/**
 * useFeatureFlag — read a single backend-driven feature flag.
 *
 * Source of truth is the sparse `user.features` map returned by
 * `GET /v1/users/me`. Universal flags (those the spec describes as available
 * to every account type, e.g. `buyer_signup`) and capability flags
 * (e.g. `wishlist_sync`) live in the same map.
 *
 * Defaults below cover the case where:
 *   1. The user is unauthenticated (no `/users/me` payload yet).
 *   2. The backend hasn't included the flag in the response — sparse maps
 *      are normal, so missing means "use FE default", not "off".
 *
 * Adding a new flag:
 *   - Pick a snake_case key matching the backend (`feature-config.ts`).
 *   - Add it to FEATURE_DEFAULTS with the FE-side default.
 *   - That's it. Read it as `useFeatureFlag('your_flag_key')` anywhere.
 *
 * Avoid hardcoding flag string keys at call sites — re-export a constant
 * from this module so the typo surface is small.
 */

export const FLAG = {
  /** Sync buyer wishlist (favorites) to backend instead of localStorage-only. */
  WISHLIST_SYNC: 'wishlist_sync',
  /** Show the "Buyer" option in the signup account-type picker. */
  BUYER_SIGNUP: 'buyer_signup',
  /** Render the curated Featured Designers hero on the home page. */
  FEATURED_DESIGNERS: 'featured_designers',
  /** Show the admin curation UI for Featured Designers (in addition to admin role). */
  FEATURED_DESIGNERS_EDITORIAL: 'featured_designers_editorial',
} as const;

export type FeatureFlagKey = (typeof FLAG)[keyof typeof FLAG] | string;

/**
 * Frontend-side defaults. Used when the backend hasn't reported a value yet
 * (anonymous users) or has chosen to omit the flag (sparse map). Backend
 * values always win when present.
 */
const FEATURE_DEFAULTS: Record<string, boolean> = {
  // Wishlist sync defaults OFF for guests — local-only favorites still work,
  // but cross-device sync needs an authed user. Once authed, the backend
  // returns this flag explicitly so behaviour follows the server.
  [FLAG.WISHLIST_SYNC]: false,
  // Buyer signup is universal per spec. Default ON so the option is visible
  // in the auth modal even before the user has authenticated. Backend can
  // turn it off globally without an FE deploy.
  [FLAG.BUYER_SIGNUP]: true,
  // Public hero is on by default — backend can disable globally without an FE deploy.
  [FLAG.FEATURED_DESIGNERS]: true,
  // Editorial UI is gated behind both this flag and an admin role check.
  [FLAG.FEATURED_DESIGNERS_EDITORIAL]: false,
};

const readFeature = (
  features: Record<string, boolean | undefined> | undefined,
  key: FeatureFlagKey,
): boolean => {
  const fromServer = features?.[key];
  if (typeof fromServer === 'boolean') return fromServer;
  return FEATURE_DEFAULTS[key] ?? false;
};

/**
 * Returns the resolved boolean value of a flag for the current user.
 * Re-renders whenever `user.features` changes.
 */
export const useFeatureFlag = (key: FeatureFlagKey): boolean => {
  const features = useSelector(
    (s: RootState) => s.auth.user?.features as Record<string, boolean | undefined> | undefined,
  );
  return readFeature(features, key);
};

/**
 * Read multiple flags in one render — handy when a component branches on
 * several flags and you want a single object instead of many hook calls.
 */
export const useFeatureFlags = <K extends FeatureFlagKey>(
  keys: readonly K[],
): Record<K, boolean> => {
  const features = useSelector(
    (s: RootState) => s.auth.user?.features as Record<string, boolean | undefined> | undefined,
  );
  const result = {} as Record<K, boolean>;
  for (const k of keys) result[k] = readFeature(features, k);
  return result;
};

/**
 * Non-hook accessor — use inside thunks, services, or anywhere outside the
 * component tree. Pass the user object (typically `state.auth.user`).
 */
export const getFeatureFlag = (
  user: { features?: Record<string, boolean | undefined> } | null | undefined,
  key: FeatureFlagKey,
): boolean => readFeature(user?.features, key);
